import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Grid,
    MenuItem,
    Button,
    Card,
    CardContent,
    CircularProgress,
} from "@mui/material";
import toast from "react-hot-toast";
import configServ from "../../services/config";

const AccountDetails = ({ companyId, employeeId }) => {

    const [loading, setLoading] = useState(false);

    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        account_holder_name: "",
        bank_name: "",
        branch_name: "",
        ifsc_code: "",
        account_type: "savings",
        bank_account_number: "",
    });

    // Handle Input
    const handleChange = (e) => {

        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Remove error while typing
        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    // Validate Form
    const validateForm = () => {

        let newErrors = {};

        if (!formData.account_holder_name.trim()) {
            newErrors.account_holder_name =
                "Account holder name is required";
        }

        if (!formData.bank_name.trim()) {
            newErrors.bank_name = "Bank name is required";
        }

        if (!formData.branch_name.trim()) {
            newErrors.branch_name = "Branch name is required";
        }

        if (!formData.bank_account_number.trim()) {
            newErrors.bank_account_number =
                "Bank account number is required";
        }

        if (!formData.ifsc_code.trim()) {
            newErrors.ifsc_code = "IFSC code is required";
        }

        if (!formData.account_type.trim()) {
            newErrors.account_type = "Account type is required";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    // Fetch Account Details
    const fetchAccountDetails = async () => {

        try {

            setLoading(true);

            const payload = {
                company_id: companyId,
                employee_id: employeeId,
            };

            const response =
                await configServ.getEmployeeAccountDetails(payload);

            if (response.status && response.data) {

                setFormData({
                    account_holder_name:
                        response.data.account_holder_name || "",

                    bank_name:
                        response.data.bank_name || "",

                    branch_name:
                        response.data.branch_name || "",

                    ifsc_code:
                        response.data.ifsc_code || "",

                    account_type:
                        response.data.account_type || "savings",

                    bank_account_number:
                        response.data.bank_account_number || "",
                });
            }

        } catch (error) {

            console.log(error);

        } finally {

            setLoading(false);
        }
    };

    // Save / Update
    const handleSubmit = async () => {

        // Stop API call if validation fails
        if (!validateForm()) {
            return;
        }

        try {

            setLoading(true);

            const payload = {
                company_id: companyId,
                employee_id: employeeId,
                ...formData,
            };

            const response =
                await configServ.saveEmployeeAccountDetails(payload);

            if (response.status) {

                toast.success(response.message);

                fetchAccountDetails();
            }

        } catch (error) {

            console.log(error);

            toast.error("Something went wrong");

        } finally {

            setLoading(false);
        }
    };

    useEffect(() => {

        if (companyId && employeeId) {
            fetchAccountDetails();
        }

    }, [companyId, employeeId]);

    return (
        <Box sx={{ py: 2 }}>

            <Card
                elevation={3}
                sx={{
                    borderRadius: 3,
                }}
            >
                <CardContent>

                    <Typography
                        variant="h6"
                        color="primary"
                        gutterBottom
                        fontWeight={600}
                    >
                        Account Details
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        Manage employee banking information.
                    </Typography>

                    <Grid container spacing={2}>

                        {/* Account Holder Name */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Account Holder Name"
                                name="account_holder_name"
                                value={formData.account_holder_name}
                                onChange={handleChange}
                                error={
                                    !!errors.account_holder_name
                                }
                                helperText={
                                    errors.account_holder_name
                                }
                            />
                        </Grid>

                        {/* Bank Name */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Bank Name"
                                name="bank_name"
                                value={formData.bank_name}
                                onChange={handleChange}
                                error={!!errors.bank_name}
                                helperText={errors.bank_name}
                            />
                        </Grid>

                        {/* Branch Name */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Branch Name"
                                name="branch_name"
                                value={formData.branch_name}
                                onChange={handleChange}
                                error={!!errors.branch_name}
                                helperText={errors.branch_name}
                            />
                        </Grid>

                        {/* Account Number */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Bank Account Number"
                                name="bank_account_number"
                                value={formData.bank_account_number}
                                onChange={handleChange}
                                error={
                                    !!errors.bank_account_number
                                }
                                helperText={
                                    errors.bank_account_number
                                }
                            />
                        </Grid>

                        {/* IFSC Code */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="IFSC Code"
                                name="ifsc_code"
                                value={formData.ifsc_code}
                                onChange={handleChange}
                                error={!!errors.ifsc_code}
                                helperText={errors.ifsc_code}
                            />
                        </Grid>

                        {/* Account Type */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Account Type"
                                name="account_type"
                                value={formData.account_type}
                                onChange={handleChange}
                                error={!!errors.account_type}
                                helperText={errors.account_type}
                            >
                                <MenuItem value="savings">
                                    Savings
                                </MenuItem>

                                <MenuItem value="current">
                                    Current
                                </MenuItem>
                            </TextField>
                        </Grid>

                    </Grid>

                    {/* Submit Button */}
                    <Box
                        sx={{
                            mt: 4,
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                minWidth: 170,
                                height: 45,
                            }}
                        >
                            {loading ? (
                                <CircularProgress
                                    size={24}
                                    color="inherit"
                                />
                            ) : (
                                "Save Details"
                            )}
                        </Button>
                    </Box>

                </CardContent>
            </Card>

        </Box>
    );
};

export default AccountDetails;